import { library } from '@fortawesome/fontawesome-svg-core';
import { faAngleLeft, faArrowLeft, faRedo, faUndo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { ReactElement } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { RowLayout } from '../../../../modules/common/layouts/rowLayout/RowLayout';
import { HiddenAnnotationsToastContainer } from '../../../containers/HiddenAnnotationsToast.container';
import { AnnotationTopBarItem } from '../../../modules/annotation/components/AnnotationTopBarItem';
import { AnnotationViewOptions } from '../../../modules/annotation/components/AnnotationViewOptions';
import { Image } from '../../../modules/annotation/components/Image';
import { DrawingContainer } from '../../../modules/annotation/containers/Drawing.container';
import { ImageContainer } from '../../../modules/annotation/containers/Image.container';
import { PreviewControlsContainer } from '../../../modules/annotation/containers/PreviewControls.container';
import { QuestionsContainer } from '../../../modules/annotation/containers/Questions.container';
import { ReturnToToolsContainer } from '../../../modules/annotation/containers/ReturnToTools.container';
import { SegmentationsContainer } from '../../../modules/annotation/containers/Segmentations.container';
import { SegmentationsSaveContainer } from '../../../modules/annotation/containers/SegmentationsSave.container';
import { UndoRedoAnnotationsShortcutsContainer } from '../../../modules/annotation/containers/UndoRedoAnnotationsShortcuts.container';
import { ViewAttributesContainer } from '../../../modules/annotation/containers/ViewAttributesContainer';
import { AnnotationTypesContainer } from '../../../modules/annotation/submodules/annotationTypes/containers/AnnotationTypes.container';
import { LeftMenuContainer } from '../../../modules/annotation/submodules/segmentations/containers/LeftMenu.container';
import { ImageSegmentationsContextProvider } from '../../../modules/annotation/submodules/segmentations/segmentationContextMenu/imageSegmentations.context';
import { AnnotationPreviewContainer } from '../../../modules/projectDetails/containers/AnnotationPreview.container';
import { Home } from '../../../routes/config/Home';

library.add(faUndo, faRedo, faAngleLeft);

export const AnnotationPreview = (): ReactElement => {
  const { t } = useTranslator();
  const history = useHistory();

  const { workspaceId, projectId } = useParams<{ projectId: string; workspaceId: string }>();

  return (
    <div className='annotation-view'>
      <AnnotationPreviewContainer>
        {(annotationPreviewProps): ReactElement => (
          <ImageSegmentationsContextProvider imageId={annotationPreviewProps.image?.id}>
            <>
              <div className='annotation-view-top-bar'>
                <AnnotationTopBarItem>
                  <Button
                    color='neutral'
                    variant='topbar'
                    tabIndex={-1}
                    onClick={() => {
                      history.push(
                        Home.Projects.Details.AnnotationView.withParams({
                          workspaceId: workspaceId,
                          projectId: projectId,
                        })
                      );
                    }}
                    startDecorator={<FontAwesomeIcon icon={faArrowLeft} />}
                  >
                    {t('back')}
                  </Button>
                </AnnotationTopBarItem>
                <AnnotationTopBarItem>
                  <UndoRedoAnnotationsShortcutsContainer undo={true} />
                </AnnotationTopBarItem>
                <AnnotationTopBarItem>
                  <UndoRedoAnnotationsShortcutsContainer undo={false} />
                </AnnotationTopBarItem>
                <AnnotationTopBarItem>
                  <AnnotationViewOptions disableCursorConfig={false} />
                </AnnotationTopBarItem>
                <AnnotationTopBarItem>
                  <ViewAttributesContainer />
                </AnnotationTopBarItem>
                <AnnotationTopBarItem variation='preview-mode'>
                  <RowLayout size='sm'>
                    <FontAwesomeIcon icon='info-circle' />
                    <div>{t('your_are_in_preview_mode')}</div>
                  </RowLayout>
                </AnnotationTopBarItem>
              </div>
              <div className='annotation-view-inner'>
                <LeftMenuContainer
                  currentProjectId={projectId}
                  isReadOnly={true}
                  showObjectsList={true}
                  isReview={false}
                />
                <HiddenAnnotationsToastContainer />
                <ImageContainer>
                  {(imageProps): ReactElement => (
                    <Image {...imageProps}>
                      <SegmentationsContainer readonly={false} />
                      <DrawingContainer disableCursorGuides={false} />
                    </Image>
                  )}
                </ImageContainer>
                <div className='right-column'>
                  <div className='scrollable-container'>
                    <ReturnToToolsContainer />
                    <div className='scrollable-container-content'>
                      <AnnotationTypesContainer />
                      <QuestionsContainer />
                      <SegmentationsSaveContainer />
                    </div>
                  </div>
                  <PreviewControlsContainer />
                </div>
              </div>
            </>
          </ImageSegmentationsContextProvider>
        )}
      </AnnotationPreviewContainer>
    </div>
  );
};
